import type { DocumentNode } from "graphql";
import { Kind, getOperationAST } from "graphql";

import {
  allocateIndividualRealTimeOrgChart,
  bulkMoveIndividualsRealTimeOrgChart,
} from "src/allocation/graphql/allocation.graphql";
import {
  addPosition,
  recreatePosition,
  updatePosition,
} from "src/allocation/graphql/members.graphql";
import { changePersonSupplyGroup } from "src/allocation/graphql/person.graphql";
import {
  markGroupAsHidden as markGroupAsHiddenAllocationTeam,
  RemoveTeamSubtreeFromOrg,
} from "src/allocation/graphql/team.graphql";
import { addAssociation as addAssociationMutation } from "src/components/AddTeamAssociationModal/queries/addAssociation.graphql";
import { updatePersonFields as updatePersonFieldsMutation } from "src/components/OrgChart/queries/updatePersonFields.graphql";
import {
  addNewTagToEntity as addNewTagToEntityTagsEditor,
  addTagToEntity as addTagToEntityTagsEditor,
  removeTagFromEntity as removeTagFromEntityTagsEditor,
  updateEntityTag as updateEntityTagTagsEditor,
  updateTagValue as updateTagValueTagsEditor,
} from "src/components/TagsEditor/queries/tags.graphql";
import { markGroupAsHidden as markGroupAsHiddenAllocation } from "src/queries/allocation.graphql";
import {
  addMembership,
  addMembershipOnly,
  fillPositionVacancy,
  removeMembership,
  updateMembership,
  vacatePosition,
} from "src/queries/memberships.graphql";
import {
  createTeam,
  moveGroupOrgChart,
  moveGroupRealTime,
  updateGroupDailyRate,
  updateGroupName,
  updateGroupType,
  updateGroupTypeRestricted,
} from "src/queries/mutations/group.graphql";
import { updatePersonAttributes } from "src/queries/mutations/updatePersonAttributes.graphql";
import { updatePersonManager } from "src/queries/mutations/updatePersonManager.graphql";
import { updateTeamAttributes } from "src/queries/mutations/updateTeamAttributes.graphql";
import {
  removeAssociation,
  updateTagValue as updateTagValueQueries,
} from "src/queries/tags.graphql";

import {
  addAssociationV2WithOption,
  addMembershipWithOption,
  addNewTagToEntityWithOption,
  addPositionWithOption,
  addTagToEntityWithOption,
  allocateIndividualRealTimeWithOption,
  bulkMoveIndividualsRealTimeWithOption,
  changePersonSupplyGroupWithOption,
  createTeamWithOption,
  fillPositionVacancyWithOption,
  markGroupAsHiddenWithOption,
  moveGroupRealTimeWithOption,
  recreatePositionWithOption,
  removeAssociationWithOption,
  removeMembershipWithOption,
  removeTagFromEntityWithOption,
  removeTeamSubtreeFromOrgWithOption,
  updateEntityTagWithOption,
  updateGroupAttributesWithOption,
  updateGroupDailyRateWithOption,
  updateGroupNameWithOption,
  updateGroupTypeRestrictedWithOption,
  updateGroupTypeWithOption,
  updateMembershipWithOption,
  updatePersonAttributesWithOption,
  updatePersonFieldsWithOption,
  updatePersonManagerWithOption,
  updatePositionWithOption,
  updateTagValueWithOption,
  vacatePositionWithOption,
} from "./designOptionGraphql";

export type OverlayMutationInventoryEntry = {
  /** Live GraphQL operation name (no WithOption suffix). */
  operation: string;
  /** JS export name of the live document in its .graphql module. */
  liveExportName: string;
  /** Relative path to the live .graphql document. */
  liveDoc: string;
  liveMutation: DocumentNode;
  twinMutation: DocumentNode;
  /** Known org-chart / Details / overlay write call sites for this live document. */
  callSites: readonly string[];
};

/**
 * How a gap is stopped from writing the baseline while an option is selected.
 * - `useOptionWritesBlocked`: the control is disabled, or the handler refuses,
 *   using `useOptionWritesBlocked` / `OPTION_WRITES_BLOCKED_MESSAGE`.
 * - `overlayBranch`: the call site reads `useOverlayOptionId` and sends the
 *   option branch through the paired twins instead of this mutation.
 */
export type OverlayGapBlocker = "useOptionWritesBlocked" | "overlayBranch";

/** Imports whose use in a `blockedAt` file proves the blocker is wired in. */
export const OVERLAY_GAP_BLOCKER_REFERENCES: Readonly<
  Record<
    OverlayGapBlocker,
    readonly { module: string; names: readonly string[] }[]
  >
> = {
  useOptionWritesBlocked: [
    {
      module: "src/pages/Designs/useOptionWritesBlocked",
      names: ["useOptionWritesBlocked", "OPTION_WRITES_BLOCKED_MESSAGE"],
    },
  ],
  overlayBranch: [
    {
      module: "src/pages/Designs/overlay",
      names: ["useOverlayOptionId"],
    },
  ],
};

/** Live mutations with no *WithOption twin — documented gaps, not paired. */
export type OverlayMutationGap = {
  operation: string;
  liveDoc: string;
  reason: string;
  callSites: readonly string[];
  blockedBy: OverlayGapBlocker;
  /**
   * Files that stop the write: each must import and use a name from
   * `OVERLAY_GAP_BLOCKER_REFERENCES[blockedBy]`. List every control that
   * triggers the mutation, or the single handler they all reach.
   */
  blockedAt: readonly string[];
  /**
   * Call sites that bind the document but do not themselves refuse the write.
   * Each needs a reason. Listing a file in `callSites` is not a block.
   */
  callSiteExceptions?: readonly { file: string; reason: string }[];
};

const pair = (
  operation: string,
  liveExportName: string,
  liveDoc: string,
  liveMutation: DocumentNode,
  twinMutation: DocumentNode,
  callSites: readonly string[]
): OverlayMutationInventoryEntry => ({
  operation,
  liveExportName,
  liveDoc,
  liveMutation,
  twinMutation,
  callSites,
});

/**
 * Authoritative inventory of live → *WithOption pairs used by org-chart and
 * Details overlay writes. Drives overlayPairing and CI completeness guards.
 */
export const OVERLAY_MUTATION_INVENTORY: OverlayMutationInventoryEntry[] = [
  pair(
    "updateGroupName",
    "updateGroupName",
    "src/queries/mutations/group.graphql",
    updateGroupName,
    updateGroupNameWithOption,
    [
      "src/pages/Designs/overlay/teamIdentityWrites.ts",
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamName.ts",
    ]
  ),
  pair(
    "createTeam",
    "createTeam",
    "src/queries/mutations/group.graphql",
    createTeam,
    createTeamWithOption,
    [
      "src/pages/Designs/overlay/teamIdentityWrites.ts",
      "src/components/OrgChart/controller/reportingLineDnd/runDropAction.ts",
    ]
  ),
  pair(
    "updateGroupType",
    "updateGroupType",
    "src/queries/mutations/group.graphql",
    updateGroupType,
    updateGroupTypeWithOption,
    []
  ),
  pair(
    "updateGroupTypeRestricted",
    "updateGroupTypeRestricted",
    "src/queries/mutations/group.graphql",
    updateGroupTypeRestricted,
    updateGroupTypeRestrictedWithOption,
    ["src/components/TeamDetailPage/ActionsMenu/Modals/ChangeTeamTypeModal.js"]
  ),
  pair(
    "updateGroupAttributes",
    "updateTeamAttributes",
    "src/queries/mutations/updateTeamAttributes.graphql",
    updateTeamAttributes,
    updateGroupAttributesWithOption,
    [
      "src/pages/Designs/overlay/teamIdentityWrites.ts",
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamAttributes.ts",
    ]
  ),
  pair(
    "updateGroupDailyRate",
    "updateGroupDailyRate",
    "src/queries/mutations/group.graphql",
    updateGroupDailyRate,
    updateGroupDailyRateWithOption,
    [
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamDailyRate.ts",
    ]
  ),
  pair(
    "updatePersonAttributes",
    "updatePersonAttributes",
    "src/queries/mutations/updatePersonAttributes.graphql",
    updatePersonAttributes,
    updatePersonAttributesWithOption,
    [
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/PersonDetails/hooks/useUpdatePersonCustomAttributes.ts",
    ]
  ),
  pair(
    "updatePersonManager",
    "updatePersonManager",
    "src/queries/mutations/updatePersonManager.graphql",
    updatePersonManager,
    updatePersonManagerWithOption,
    [
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamOwner.ts",
      "src/components/OrgChart/controller/dnd/useOrgChartDnDDragSession.ts",
      "src/components/OrgChart/controller/reportingLineDnd/runDropAction.ts",
    ]
  ),
  pair(
    "changePersonSupplyGroup",
    "changePersonSupplyGroup",
    "src/allocation/graphql/person.graphql",
    changePersonSupplyGroup,
    changePersonSupplyGroupWithOption,
    [
      "src/components/OrgChart/controller/dnd/useOrgChartDnDHoldingPen.tsx",
      "src/hooks/useAllocatePersonToGroup.tsx",
    ]
  ),
  pair(
    "markGroupAsHidden",
    "markGroupAsHidden",
    "src/allocation/graphql/team.graphql",
    markGroupAsHiddenAllocationTeam,
    markGroupAsHiddenWithOption,
    [
      "src/components/HideTeamModal/index.js",
      "src/components/OrgChart/controller/dnd/useOrgChartDnDHoldingPen.tsx",
    ]
  ),
  pair(
    "markGroupAsHidden",
    "markGroupAsHidden",
    "src/queries/allocation.graphql",
    markGroupAsHiddenAllocation,
    markGroupAsHiddenWithOption,
    []
  ),
  pair(
    "removeTeamSubtreeFromOrg",
    "RemoveTeamSubtreeFromOrg",
    "src/allocation/graphql/team.graphql",
    RemoveTeamSubtreeFromOrg,
    removeTeamSubtreeFromOrgWithOption,
    ["src/components/OrgChart/controller/dnd/useOrgChartDnDCore.ts"]
  ),
  pair(
    "bulkMoveIndividualsRealTime",
    "bulkMoveIndividualsRealTimeOrgChart",
    "src/allocation/graphql/allocation.graphql",
    bulkMoveIndividualsRealTimeOrgChart,
    bulkMoveIndividualsRealTimeWithOption,
    [
      "src/components/BulkMoveMembersModal/hooks/useBulkMoveMembers.js",
      "src/components/OrgChart/controller/dnd/useAllocatePersonToTeam.ts",
      "src/components/OrgChart/controller/reportingLineDnd/runDropAction.ts",
    ]
  ),
  pair(
    "allocateIndividualRealTime",
    "allocateIndividualRealTimeOrgChart",
    "src/allocation/graphql/allocation.graphql",
    allocateIndividualRealTimeOrgChart,
    allocateIndividualRealTimeWithOption,
    ["src/components/OrgChart/controller/dnd/useOrgChartDnDHoldingPen.tsx"]
  ),
  pair(
    "updateEntityTag",
    "updateEntityTag",
    "src/components/TagsEditor/queries/tags.graphql",
    updateEntityTagTagsEditor,
    updateEntityTagWithOption,
    ["src/components/TagsEditor/sub-components/TagAttributesModal.tsx"]
  ),
  pair(
    "moveGroupRealTime",
    "moveGroupRealTime",
    "src/queries/mutations/group.graphql",
    moveGroupRealTime,
    moveGroupRealTimeWithOption,
    [
      "src/components/MoveGroupModal/index.js",
      "src/components/MoveGroupEnhancedModal/index.js",
    ]
  ),
  pair(
    "moveGroupRealTime",
    "moveGroupOrgChart",
    "src/queries/mutations/group.graphql",
    moveGroupOrgChart,
    moveGroupRealTimeWithOption,
    [
      "src/components/OrgChart/controller/dnd/useOrgChartDnDCore.ts",
      "src/components/OrgChart/controller/reportingLineDnd/runDropAction.ts",
    ]
  ),
  pair(
    "addMembership",
    "addMembership",
    "src/queries/memberships.graphql",
    addMembership,
    addMembershipWithOption,
    [
      "src/components/PersonDetailPage/PersonMemberships/hooks/useMemberships.js",
    ]
  ),
  pair(
    "addMembership",
    "addMembershipOnly",
    "src/queries/memberships.graphql",
    addMembershipOnly,
    addMembershipWithOption,
    [
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamOwner.ts",
      "src/components/OrgChart/controller/dnd/useOrgChartDnDWrites.tsx",
      "src/components/OrgChart/controller/reportingLineDnd/runDropAction.ts",
      "src/pages/Designs/overlay/useOverlayRosterAdd.ts",
      "src/hooks/useAllocatePersonToGroup.tsx",
      "src/components/AddPositionModal/index.js",
    ]
  ),
  pair(
    "removeMembership",
    "removeMembership",
    "src/queries/memberships.graphql",
    removeMembership,
    removeMembershipWithOption,
    [
      "src/components/PersonDetailPage/PersonMemberships/hooks/useMemberships.js",
      "src/components/OrgChart/utils/removeOwnerMembership.ts",
    ]
  ),
  pair(
    "updateMembership",
    "updateMembership",
    "src/queries/memberships.graphql",
    updateMembership,
    updateMembershipWithOption,
    [
      "src/components/PersonDetailPage/PersonMemberships/hooks/useMemberships.js",
      "src/components/BulkMoveMembersModal/hooks/useBulkMoveMembers.js",
      "src/components/OrgChart/controller/dnd/orgChartMembershipRemoval.ts",
    ]
  ),
  pair(
    "fillPositionVacancy",
    "fillPositionVacancy",
    "src/queries/memberships.graphql",
    fillPositionVacancy,
    fillPositionVacancyWithOption,
    [
      "src/components/PersonDetailPage/PersonMemberships/hooks/useFillPosition.js",
      "src/components/PersonDetailPage/PersonMemberships/hooks/useMemberships.js",
      "src/components/OrgChart/controller/dnd/useOrgChartDnDCore.ts",
    ]
  ),
  pair(
    "vacatePosition",
    "vacatePosition",
    "src/queries/memberships.graphql",
    vacatePosition,
    vacatePositionWithOption,
    [
      "src/components/PersonDetailPage/PersonMemberships/hooks/useVacatePosition.ts",
      "src/components/PersonDetailPage/PersonMemberships/hooks/useMemberships.js",
      "src/components/OrgChart/controller/dnd/useOrgChartDnDCore.ts",
    ]
  ),
  pair(
    "addPosition",
    "addPosition",
    "src/allocation/graphql/members.graphql",
    addPosition,
    addPositionWithOption,
    ["src/components/AddPositionModal/index.js"]
  ),
  pair(
    "recreatePosition",
    "recreatePosition",
    "src/allocation/graphql/members.graphql",
    recreatePosition,
    recreatePositionWithOption,
    [
      "src/components/OrgChart/controller/dnd/useOrgChartDnDCore.ts",
      "src/components/BulkMoveMembersModal/hooks/teamDetailsMove.ts",
    ]
  ),
  pair(
    "updatePosition",
    "updatePosition",
    "src/allocation/graphql/members.graphql",
    updatePosition,
    updatePositionWithOption,
    [
      "src/components/EditPositionModal/index.js",
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamOwner.ts",
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/PersonDetails/hooks/useUpdateDetailsFte.ts",
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/PersonDetails/hooks/useUpdatePositionName.ts",
      "src/components/OrgChart/controller/dnd/useOrgChartDnDDragSession.ts",
      "src/components/OrgChart/controller/dnd/useOrgChartDnDWrites.tsx",
      "src/components/OrgChart/controller/reportingLineDnd/runDropAction.ts",
    ]
  ),
  pair(
    "addTagToEntity",
    "addTagToEntity",
    "src/components/TagsEditor/queries/tags.graphql",
    addTagToEntityTagsEditor,
    addTagToEntityWithOption,
    [
      "src/components/TagsEditor/TagsEditorContainer.tsx",
      "src/components/AddVacantRoleModal/index.js",
      "src/pages/TagDetailsPage/sub-components/hooks/useAddTagToEntity.js",
    ]
  ),
  pair(
    "addNewTagToEntity",
    "addNewTagToEntity",
    "src/components/TagsEditor/queries/tags.graphql",
    addNewTagToEntityTagsEditor,
    addNewTagToEntityWithOption,
    ["src/components/TagsEditor/TagsEditorContainer.tsx"]
  ),
  pair(
    "removeTagFromEntity",
    "removeTagFromEntity",
    "src/components/TagsEditor/queries/tags.graphql",
    removeTagFromEntityTagsEditor,
    removeTagFromEntityWithOption,
    ["src/components/TagsEditor/TagsEditorContainer.tsx"]
  ),
  pair(
    "addAssociationV2",
    "addAssociation",
    "src/components/AddTeamAssociationModal/queries/addAssociation.graphql",
    addAssociationMutation,
    addAssociationV2WithOption,
    ["src/components/AddTeamAssociationModal/index.js"]
  ),
  pair(
    "removeAssociation",
    "removeAssociation",
    "src/queries/tags.graphql",
    removeAssociation,
    removeAssociationWithOption,
    [
      "src/components/AddTeamAssociationModal/index.js",
      "src/components/TeamAssociationsPanel/RemoveAssociationModal.js",
    ]
  ),
  pair(
    "updatePersonFields",
    "updatePersonFields",
    "src/components/OrgChart/queries/updatePersonFields.graphql",
    updatePersonFieldsMutation,
    updatePersonFieldsWithOption,
    [
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/PersonDetails/hooks/useUpdatePersonFields.ts",
    ]
  ),
  pair(
    "updateTag",
    "updateTagValue",
    "src/components/TagsEditor/queries/tags.graphql",
    updateTagValueTagsEditor,
    updateTagValueWithOption,
    ["src/components/TagsEditor/sub-components/TagAttributesModal.tsx"]
  ),
  pair(
    "updateTag",
    "updateTagValue",
    "src/queries/tags.graphql",
    updateTagValueQueries,
    updateTagValueWithOption,
    ["src/pages/TagDetailsPage/sub-components/hooks/useTagBgImage.js"]
  ),
];

/**
 * Same-document option writes. The Apollo guard forwards these only when
 * `optionId` is the selected option; omitting it while an option is selected
 * still refuses the baseline write.
 */
export const OVERLAY_OPTION_ARGUMENT_MUTATIONS: readonly {
  operation: string;
  liveDoc: string;
  reason: string;
}[] = [
  {
    operation: "mutateMemberships",
    liveDoc: "src/queries/memberships.graphql",
    reason:
      "Batch membership writes pass optionId on mutateMemberships. Single-row edits stay on the *WithOption twins.",
  },
  {
    operation: "syncForecastPositionDemandMemberships",
    liveDoc: "src/allocation/graphql/demandRequest.graphql",
    reason:
      "Demand position moves and removals pass optionId through mutateMemberships.",
  },
];

/** Live mutations with no *WithOption twin — documented gaps, not paired. */
export const OVERLAY_MUTATION_GAPS: OverlayMutationGap[] = [
  {
    operation: "addNewVacantRole",
    liveDoc: "src/allocation/graphql/members.graphql",
    reason:
      "Legacy placeholder-person role add; positions use addPositionWithOption.",
    callSites: [
      "src/components/AddPositionModal/index.js",
      "src/components/AddVacantRoleModal/index.js",
    ],
    blockedBy: "useOptionWritesBlocked",
    blockedAt: [
      "src/components/AddVacantRoleModal/index.js",
      "src/components/AddPositionModal/index.js",
      "src/components/TeamDetailPage/ActionsMenu/hooks/useTeamActions.tsx",
      "src/hooks/useVacantRolePresetOption.js",
    ],
  },
  {
    operation: "addNewTag",
    liveDoc: "src/components/TagsEditor/queries/tags.graphql",
    reason: "Bulk tag create has no *WithOption twin.",
    callSites: ["src/components/TagsEditor/useBulkAddTag.ts"],
    blockedBy: "useOptionWritesBlocked",
    blockedAt: [
      "src/components/TagsEditor/BulkTagsEditorContainer.tsx",
      "src/components/TagsEditor/useBulkAddTag.ts",
    ],
  },
  {
    operation: "updateTagsOnEntities",
    liveDoc: "src/components/TagsEditor/queries/tags.graphql",
    reason: "Bulk tag apply/remove has no *WithOption twin.",
    callSites: [
      "src/components/TagsEditor/useBulkAddTag.ts",
      "src/components/TagsEditor/useBulkRemoveTag.ts",
    ],
    blockedBy: "useOptionWritesBlocked",
    blockedAt: [
      "src/components/TagsEditor/BulkTagsEditorContainer.tsx",
      "src/components/TagsEditor/useBulkAddTag.ts",
      "src/components/TagsEditor/useBulkRemoveTag.ts",
    ],
  },
  {
    operation: "upsertTagProperties",
    liveDoc: "src/queries/mutations/upsertTagProperties.graphql",
    reason: "Tag background image save has no *WithOption twin.",
    callSites: [
      "src/components/BackgroundImageContainer/hooks/useUpdateBackground.js",
    ],
    blockedBy: "useOptionWritesBlocked",
    blockedAt: ["src/components/BackgroundImageContainer/index.js"],
    callSiteExceptions: [
      {
        file: "src/components/BackgroundImageContainer/hooks/useUpdateBackground.js",
        reason:
          "Binds upsertTagProperties only. BackgroundImageContainer, its only caller, replaces the picker with the blocked message while an option is selected.",
      },
    ],
  },
  {
    operation: "selectPrincipleSet",
    liveDoc: "src/pages/TeamDetailPage/team.graphql",
    reason: "Team principle set selection has no *WithOption twin.",
    callSites: ["src/pages/TeamDetailPage/PrinciplesTabPage.js"],
    blockedBy: "useOptionWritesBlocked",
    blockedAt: [
      "src/components/TeamDetailPage/Principles/PrincipleSetSelector.js",
      "src/pages/TeamDetailPage/PrinciplesTabPage.js",
    ],
  },
  {
    operation: "cloneHierarchy",
    liveDoc: "src/queries/allocation.graphql",
    reason: "Hierarchy clone has no *WithOption twin.",
    callSites: ["src/components/CloneHierarchyModal/index.js"],
    blockedBy: "useOptionWritesBlocked",
    blockedAt: [
      "src/components/CloneHierarchyModal/index.js",
      "src/components/TeamDetailPage/ActionsMenu/hooks/useTeamActions.tsx",
      "src/pages/TeamPage/sub-components/TeamHeaderPanel/index.js",
    ],
  },
  {
    operation: "fulfillVacantRole",
    liveDoc: "src/queries/allocation.graphql",
    reason:
      "Legacy vacancy fulfil has no *WithOption twin; positions use fillPositionVacancyWithOption.",
    callSites: [
      "src/components/FulfillVacantRole/hooks/useFulfillVacantRole.ts",
    ],
    blockedBy: "useOptionWritesBlocked",
    blockedAt: [
      "src/components/FulfillVacantRole/hooks/useFulfillVacantRole.ts",
      "src/components/Person/PersonItem/sub-components/hooks/useFulfillVacantRole.js",
      "src/components/NodeVisualizer/Nodes/PersonNode/PersonActionsMenu.tsx",
      "src/components/Person/PersonItem/sub-components/PersonAvatarAndDetails.js",
    ],
  },
  {
    operation: "removePlaceholderPerson",
    liveDoc: "src/queries/person.graphql",
    reason: "Placeholder person removal has no *WithOption twin.",
    callSites: [
      "src/components/Person/PersonItem/sub-components/hooks/useFulfillVacantRole.js",
    ],
    blockedBy: "useOptionWritesBlocked",
    blockedAt: [
      "src/components/Person/PersonItem/sub-components/hooks/useFulfillVacantRole.js",
      "src/components/NodeVisualizer/Nodes/PersonNode/PersonActionsMenu.tsx",
      "src/components/Person/PersonItem/sub-components/PersonAvatarAndDetails.js",
    ],
  },
  {
    operation: "cancelPosition",
    liveDoc: "src/queries/person.graphql",
    reason: "Position cancel has no *WithOption twin.",
    callSites: ["src/components/CancelOpenPosition/useCancelPosition.ts"],
    blockedBy: "useOptionWritesBlocked",
    blockedAt: [
      "src/components/CancelOpenPosition/useCancelPosition.ts",
      "src/components/CancelOpenPosition/CancelOpenPositionAction.tsx",
    ],
  },
];

/** All overlay *WithOption operation names declared in withOptionMutations.graphql. */
export const OVERLAY_TWIN_OPERATIONS = [
  "updateGroupNameWithOption",
  "createTeamWithOption",
  "updateGroupTypeWithOption",
  "updateGroupTypeRestrictedWithOption",
  "updateGroupAttributesWithOption",
  "updateGroupDailyRateWithOption",
  "updatePersonAttributesWithOption",
  "updatePersonManagerWithOption",
  "changePersonSupplyGroupWithOption",
  "markGroupAsHiddenWithOption",
  "removeTeamSubtreeFromOrgWithOption",
  "bulkMoveIndividualsRealTimeWithOption",
  "allocateIndividualRealTimeWithOption",
  "updateEntityTagWithOption",
  "moveGroupRealTimeWithOption",
  "addMembershipWithOption",
  "removeMembershipWithOption",
  "updateMembershipWithOption",
  "fillPositionVacancyWithOption",
  "vacatePositionWithOption",
  "recreatePositionWithOption",
  "addPositionWithOption",
  "updatePositionWithOption",
  "addTagToEntityWithOption",
  "addNewTagToEntityWithOption",
  "removeTagFromEntityWithOption",
  "addAssociationV2WithOption",
  "removeAssociationWithOption",
  "updatePersonFieldsWithOption",
  "updateTagValueWithOption",
] as const;

/** Resolve the mutation operation name from a GraphQL document (not definitions[0]). */
export const getMutationOperationName = (
  document: DocumentNode
): string | undefined => {
  const fromAst = getOperationAST(document)?.name?.value;
  if (fromAst) return fromAst;

  for (const definition of document.definitions) {
    if (
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.name?.value
    ) {
      return definition.name.value;
    }
  }
  return undefined;
};

/**
 * Modules (extension and `/index` stripped) and the names that route a write
 * through overlayMutate. `BoundOverlayMutate` is the type of a router injected
 * into helpers such as teamIdentityWrites.
 */
export const OVERLAY_WRITE_ROUTERS: readonly {
  module: string;
  names: readonly string[];
}[] = [
  {
    module: "src/pages/Designs/overlay",
    names: ["overlayMutate", "useOverlayMutate", "useTeamIdentityWrites"],
  },
  {
    module: "src/pages/Designs/overlay/overlayMutate",
    names: ["overlayMutate", "BoundOverlayMutate"],
  },
  {
    module: "src/pages/Designs/overlay/useOverlayMutate",
    names: ["useOverlayMutate"],
  },
  {
    module: "src/pages/Designs/overlay/useTeamIdentityWrites",
    names: ["useTeamIdentityWrites"],
  },
  {
    module:
      "src/components/OrgChart/components/OrgChartPanel/panels/Details/PersonDetails/hooks/usePersonDetailsMutation",
    names: ["usePersonDetailsMutation"],
  },
];

export type OverlayUnroutedAllowance = {
  reason: string;
  /**
   * `imported`: the file imports an overlayMutate router for the option branch
   * and keeps the live doc for the trunk branch (checked by the inventory test).
   * `injected`: a caller passes the bound router in as a parameter.
   * `none`: a known gap. The file writes trunk with an option selected.
   */
  routing: "imported" | "injected" | "none";
};

/** Files that may use an inventory live doc without handing it to overlayMutate. */
export const OVERLAY_UNROUTED_LIVE_DOC_ALLOWLIST: Readonly<
  Record<string, OverlayUnroutedAllowance>
> = {
  "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamOwner.ts":
    {
      routing: "imported",
      reason:
        "Option branch routes through overlayMutate; live docs are the trunk branch.",
    },
  "src/components/OrgChart/utils/removeOwnerMembership.ts": {
    routing: "imported",
    reason:
      "Option branch routes through overlayMutate; live doc is the trunk branch.",
  },
  "src/components/OrgChart/controller/dnd/useOrgChartDnDCore.ts": {
    routing: "imported",
    reason:
      "useMutation holds each live doc; callers branch to overlayMutate when an option is selected.",
  },
  "src/components/OrgChart/controller/reportingLineDnd/runDropAction.ts": {
    routing: "imported",
    reason:
      "useMutation holds each live doc; the drop action branches to overlayMutate when an option is selected.",
  },
  "src/components/TagsEditor/sub-components/TagAttributesModal.persist.ts": {
    routing: "injected",
    reason: "TagAttributesModal passes its overlayMutate in as a parameter.",
  },
  "src/components/TagsEditor/useTagEntityMutations.ts": {
    routing: "injected",
    reason: "TagsEditorContainer passes its overlayMutate in as a parameter.",
  },
  "src/hooks/useAllocatePersonToGroup.tsx": {
    routing: "injected",
    reason:
      "Overlay roster add injects overlayMutate; demand allocate omits it.",
  },
  "src/components/BackgroundImageContainer/hooks/useUpdateBackground.js": {
    routing: "none",
    reason:
      "Saves call updatePersonAttributes/updateTeamAttributes on trunk. BackgroundImageContainer, its only caller, replaces the picker with the blocked message while an option is selected.",
  },
};

export const buildOverlayTwinMap = (): Map<DocumentNode, DocumentNode> => {
  const map = new Map<DocumentNode, DocumentNode>();
  for (const entry of OVERLAY_MUTATION_INVENTORY) {
    map.set(entry.liveMutation, entry.twinMutation);
  }
  return map;
};

export const allOverlayWriteCallSites = (): string[] =>
  Array.from(
    new Set(OVERLAY_MUTATION_INVENTORY.flatMap((entry) => [...entry.callSites]))
  ).sort();
